import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  ViewChild
} from '@angular/core';

// Customer signature pad, mirroring S2's DocumentSignatureSignComponent: draw on a
// canvas (mouse + touch), press "Guardar Firma" to freeze it into a preview, and it
// emits the PNG data: URL (the app CSP allows img-src data:, not blob:). "Limpiar"
// resets and emits null. An empty stroke cannot be saved (hasDrawn guard).
@Component({
  selector: 'app-signature-canvas',
  templateUrl: './signature-canvas.component.html',
  styleUrls: ['./signature-canvas.component.css'],
  standalone: false
})
export class SignatureCanvasComponent implements AfterViewInit {
  @Output() saved = new EventEmitter<string | null>();
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  isDrawing = false;
  signatureImage: string | null = null;

  private ctx!: CanvasRenderingContext2D;
  private hasDrawn = false;
  private lastWidth = window.innerWidth;
  private lastHeight = window.innerHeight;

  ngAfterViewInit(): void {
    const context = this.canvasRef.nativeElement.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context is not available');
    }
    this.ctx = context;
    this.ctx.lineCap = 'round';
    this.ctx.lineWidth = 2;
    this.ctx.lineJoin = 'round';
    this.adjustCanvasSize();
  }

  startDrawing(event: MouseEvent | TouchEvent): void {
    if (!this.ctx || !this.canvasRef) {
      return;
    }
    event.preventDefault();
    this.isDrawing = true;
    this.hasDrawn = false;
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const point = this.getPointerPosition(event);
    this.ctx.beginPath();
    this.ctx.moveTo(point.x - rect.left, point.y - rect.top);
  }

  draw(event: MouseEvent | TouchEvent): void {
    if (!this.isDrawing || !this.ctx || !this.canvasRef) {
      return;
    }
    event.preventDefault();
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const point = this.getPointerPosition(event);
    this.ctx.lineTo(point.x - rect.left, point.y - rect.top);
    this.ctx.stroke();
    this.hasDrawn = true;
  }

  endDrawing(): void {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  saveAsImage(): void {
    if (!this.hasDrawn) {
      window.alert('No se ha realizado ninguna firma');
      return;
    }
    this.signatureImage = this.canvasRef.nativeElement.toDataURL('image/png');
    this.saved.emit(this.signatureImage);
  }

  deleteDraw(): void {
    const canvas = this.canvasRef.nativeElement;
    this.signatureImage = null;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.hasDrawn = false;
    this.saved.emit(null);
  }

  @HostListener('window:resize')
  onResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientationChanged =
      (this.lastWidth > this.lastHeight && width < height) ||
      (this.lastWidth < this.lastHeight && width > height);
    if (orientationChanged && !this.signatureImage) {
      this.adjustCanvasSize();
    }
    this.lastWidth = width;
    this.lastHeight = height;
  }

  private adjustCanvasSize(): void {
    const canvas = this.canvasRef.nativeElement;
    const container = canvas.parentElement;
    if (!container) {
      return;
    }
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    // Re-applying context props (a resize resets the backing store).
    this.ctx.lineCap = 'round';
    this.ctx.lineWidth = 2;
    this.ctx.lineJoin = 'round';
  }

  private getPointerPosition(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if (event instanceof MouseEvent) {
      return { x: event.clientX, y: event.clientY };
    }
    const touch = event.touches[0] || event.changedTouches[0];
    return { x: touch.clientX, y: touch.clientY };
  }
}
